import React, { useState } from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    LinearProgress,
    IconButton,
    Button,
    Card,
    CardContent,
    Grid,
    Avatar,
    AvatarGroup,
    Tooltip,
    Stack,
    InputAdornment,
    TextField,
    Dialog,
    DialogContent
} from '@mui/material';
import {
    Visibility,
    ArrowBack,
    CheckCircle,
    RadioButtonUnchecked,
    AccessTime,
    CalendarToday,
    PriorityHigh,
    Close,
    Search,
    Add
} from '@mui/icons-material';
import UserStoryDetailsDummy from './UserStoryDetailsDummy';
import CreateUserStory from './CreateUserStory';

// Sample data for user stories
const initialEpics = [
    { 
        id: 1, 
        name: 'Share-Ur-Truck App', 
        totalStories: 8, 
        color: '#2196f3',
        projectName: 'Mobile Development Project'
    },
    { 
        id: 2, 
        name: 'CommuniLink Beta', 
        totalStories: 4, 
        color: '#4caf50',
        projectName: 'Communication Platform'
    },
];

const initialUserStories = [
    {
        id: 1,
        epicId: 1,
        story_id: 'US-001',
        name: 'Location Validation for Delivery Zones',
        epic: 'Share-Ur-Truck App',
        project: 'Mobile Development Project',
        priority: 'Medium',
        progress: 71,
        dueDate: '2025-12-15',
        status: 'Ready for Dev',
        tasks: 5,
        completedTasks: 3,
        assignees: ['JD', 'MP', 'AL']
    },
    {
        id: 2,
        epicId: 1,
        story_id: 'US-002',
        name: 'Real-time GPS Tracking Integration',
        epic: 'Share-Ur-Truck App',
        project: 'Mobile Development Project',
        priority: 'High',
        progress: 0,
        dueDate: '2025-11-20',
        status: 'In Development',
        tasks: 8,
        completedTasks: 0,
        assignees: ['JD']
    },
    {
        id: 3,
        epicId: 2,
        story_id: 'US-003',
        name: 'User Authentication System',
        epic: 'CommuniLink Beta',
        project: 'Communication Platform',
        priority: 'Medium',
        progress: 0,
        dueDate: '2025-11-10',
        status: 'Ready for Dev',
        tasks: 3,
        completedTasks: 0,
        assignees: ['SM', 'AL']
    },
    {
        id: 4,
        epicId: 1,
        story_id: 'US-004',
        name: 'Payment Gateway Integration',
        epic: 'Share-Ur-Truck App',
        project: 'Mobile Development Project',
        priority: 'High',
        progress: 45,
        dueDate: '2025-12-01',
        status: 'In Development',
        tasks: 7,
        completedTasks: 3,
        assignees: ['MP', 'RK']
    },
    {
        id: 5,
        epicId: 2,
        story_id: 'US-005',
        name: 'Chat System with File Sharing',
        epic: 'CommuniLink Beta',
        project: 'Communication Platform',
        priority: 'Low',
        progress: 100,
        dueDate: '2025-10-15',
        status: 'Completed',
        tasks: 12,
        completedTasks: 12,
        assignees: ['SM', 'JD', 'AL']
    },
];

// Sample tasks data
const tasksData = {
    1: [
        { id: 1, name: 'Define delivery zone boundaries', status: 'Completed', assignee: 'JD', estimatedHours: 8 },
        { id: 2, name: 'Implement geofencing logic', status: 'In Progress', assignee: 'MP', estimatedHours: 16 },
        { id: 3, name: 'Create validation API endpoints', status: 'Completed', assignee: 'AL', estimatedHours: 12 },
        { id: 4, name: 'Test with real location data', status: 'To Do', assignee: 'JD', estimatedHours: 8 },
        { id: 5, name: 'Update user interface for validation feedback', status: 'To Do', assignee: 'MP', estimatedHours: 10 },
    ],
    2: [
        { id: 1, name: 'Integrate GPS tracking SDK', status: 'To Do', assignee: 'JD', estimatedHours: 20 },
        { id: 2, name: 'Create real-time location updates', status: 'To Do', assignee: 'JD', estimatedHours: 24 },
        { id: 3, name: 'Optimize battery usage', status: 'To Do', assignee: 'JD', estimatedHours: 16 },
    ],
    3: [
        { id: 1, name: 'Design login/signup flow', status: 'To Do', assignee: 'SM', estimatedHours: 12 },
        { id: 2, name: 'Implement OAuth2 integration', status: 'To Do', assignee: 'AL', estimatedHours: 18 },
        { id: 3, name: 'Add multi-factor authentication', status: 'To Do', assignee: 'SM', estimatedHours: 10 },
    ]
};

const UserStoryTable = () => {
    const [selectedEpic, setSelectedEpic] = useState(null);
    const [selectedStory, setSelectedStory] = useState(null);
    const [viewMode, setViewMode] = useState('stories'); // 'stories', 'tasks', or 'details'
    const [searchQuery, setSearchQuery] = useState('');
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [currentEpicData, setCurrentEpicData] = useState(null);

    // Filter stories based on selected epic and search
    const filteredStories = initialUserStories.filter(story => {
        const matchesEpic = !selectedEpic || story.epicId === selectedEpic;
        const matchesSearch = !searchQuery ||
            story.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            story.epic.toLowerCase().includes(searchQuery.toLowerCase());
        return matchesEpic && matchesSearch;
    });

    // Status counts - only for current epic if selected
    const statusCounts = {
        total: filteredStories.length,
        ready: filteredStories.filter(s => s.status === 'Ready for Dev').length,
        development: filteredStories.filter(s => s.status === 'In Development').length,
        completed: filteredStories.filter(s => s.status === 'Completed').length,
    };

    // Get priority chip color
    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'High': return 'error';
            case 'Medium': return 'warning';
            case 'Low': return 'success';
            default: return 'default';
        }
    };

    // Get status chip color
    const getStatusColor = (status) => {
        switch (status) {
            case 'Ready for Dev': return 'primary';
            case 'In Development': return 'secondary';
            case 'Completed': return 'success';
            default: return 'default';
        }
    };

    // Handle clicking on a story row - this now sets the epic context
    const handleStoryRowClick = (story) => {
        // Set the epic context based on the story clicked
        const epic = initialEpics.find(e => e.id === story.epicId);
        if (epic) {
            setSelectedEpic(story.epicId);
            setCurrentEpicData({
                epicName: story.epic,
                projectName: story.project,
                color: epic.color
            });
        }
        // Also open the story details
        setSelectedStory(story.id);
        setViewMode('details');
    };

    // Handle viewing user story details from action button
    const handleViewStoryDetails = (storyId, e) => {
        if (e) e.stopPropagation();
        const story = initialUserStories.find(s => s.id === storyId);
        if (story) {
            setSelectedStory(storyId);
            setViewMode('details');
            // Also set epic context if not already set
            if (!selectedEpic || selectedEpic !== story.epicId) {
                const epic = initialEpics.find(e => e.id === story.epicId);
                if (epic) {
                    setSelectedEpic(story.epicId);
                    setCurrentEpicData({
                        epicName: story.epic,
                        projectName: story.project,
                        color: epic.color
                    });
                }
            }
        }
    };

    // Handle going back to stories view
    const handleBackToStories = () => {
        setViewMode('stories');
        setSelectedStory(null);
        // Keep the epic context when going back
    };

    // Clear epic filter
    const handleClearEpic = () => {
        setSelectedEpic(null);
        setCurrentEpicData(null);
        setSearchQuery('');
    };

    // Clear search query
    const handleClearSearch = () => {
        setSearchQuery('');
    };

    // Get selected story details
    const currentStory = initialUserStories.find(story => story.id === selectedStory);

    // Handle story deletion
    const handleStoryDeleted = () => {
        handleBackToStories();
    };

    // Close story details
    const handleCloseStoryDetails = () => {
        handleBackToStories();
    };

    // Handle creating new user story
    const handleCreateUserStory = () => {
        setCreateDialogOpen(true);
    };

    const handleCloseCreateDialog = () => {
        setCreateDialogOpen(false);
    };

    const handleUserStoryCreated = (newStory) => {
        if (newStory) {
            console.log('New user story created:', newStory);
        }
        setCreateDialogOpen(false);
    };

    return (
        <Box sx={{ px: 2 }}>
            {/* Header with epic/project name chip - Only show in stories view */}
            {viewMode === 'stories' && (
                <Box sx={{ mb: 2 }}>
                    {/* Top row: Title with epic/project chip and Search Bar */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Typography variant="h5" fontWeight="bold">
                                User Stories
                            </Typography>
                            {currentEpicData && (
                                <Chip
                                    label={`${currentEpicData.epicName} • ${currentEpicData.projectName}`}
                                    onDelete={handleClearEpic}
                                    sx={{
                                        backgroundColor: currentEpicData.color,
                                        color: 'white',
                                        fontWeight: 500,
                                        height: 28,
                                        fontSize: '0.8rem',
                                        '& .MuiChip-deleteIcon': {
                                            color: 'white',
                                            fontSize: '16px',
                                            '&:hover': { color: '#f0f0f0' }
                                        }
                                    }}
                                />
                            )}
                        </Box>
                        
                        {/* Search Bar and Create Button */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TextField
                                placeholder="Search by Story Name..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                size="small"
                                sx={{
                                    width: '250px',
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '6px',
                                        fontSize: '13px',
                                        height: '36px',
                                    }
                                }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search sx={{ color: 'text.secondary', fontSize: 18 }} />
                                        </InputAdornment>
                                    ),
                                    endAdornment: searchQuery && (
                                        <InputAdornment position="end">
                                            <IconButton
                                                size="small"
                                                onClick={handleClearSearch}
                                                sx={{ p: 0.25 }}
                                            >
                                                <Close sx={{ fontSize: 14 }} />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <Button
                                variant="contained"
                                size="small"
                                startIcon={<Add />}
                                onClick={handleCreateUserStory}
                                sx={{
                                    backgroundColor: '#3b82f6',
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    px: 2,
                                    py: 0.75,
                                    fontSize: '0.85rem',
                                    height: '36px',
                                    '&:hover': { backgroundColor: '#2563eb' }
                                }}
                            >
                                New User Story
                            </Button>
                        </Box>
                    </Box>

                    {/* Status Cards - Compact */}
                    <Grid container spacing={1.5} sx={{ mb: 2 }}>
                        <Grid item xs={3}>
                            <Card sx={{ p: 1, textAlign: 'center', bgcolor: '#f5f5f5', borderRadius: '6px' }}>
                                <Typography variant="h6" sx={{ fontSize: '1.1rem', mb: 0.5 }}>{statusCounts.total}</Typography>
                                <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem' }}>
                                    {currentEpicData ? 'Epic Stories' : 'Total Stories'}
                                </Typography>
                            </Card>
                        </Grid>
                        <Grid item xs={3}>
                            <Card sx={{ p: 1, textAlign: 'center', bgcolor: '#e3f2fd', borderRadius: '6px' }}>
                                <Typography variant="h6" sx={{ fontSize: '1.1rem', mb: 0.5 }}>{statusCounts.ready}</Typography>
                                <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem' }}>Ready for Dev</Typography>
                            </Card>
                        </Grid>
                        <Grid item xs={3}>
                            <Card sx={{ p: 1, textAlign: 'center', bgcolor: '#f3e5f5', borderRadius: '6px' }}>
                                <Typography variant="h6" sx={{ fontSize: '1.1rem', mb: 0.5 }}>{statusCounts.development}</Typography>
                                <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem' }}>In Development</Typography>
                            </Card>
                        </Grid>
                        <Grid item xs={3}>
                            <Card sx={{ p: 1, textAlign: 'center', bgcolor: '#e8f5e9', borderRadius: '6px' }}>
                                <Typography variant="h6" sx={{ fontSize: '1.1rem', mb: 0.5 }}>{statusCounts.completed}</Typography>
                                <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem' }}>Completed</Typography>
                            </Card>
                        </Grid>
                    </Grid>
                </Box>
            )}

            {/* Main Content Area */}
            {viewMode === 'stories' ? (
                /* Stories Table View */
                <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 1 }}>
                    <Table sx={{ minWidth: 650 }} size="small">
                        <TableHead sx={{ bgcolor: '#fafafa' }}>
                            <TableRow sx={{ height: '40px' }}>
                                <TableCell width="25%" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1 }}>Story Name</TableCell>
                                <TableCell width="10%" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1 }}>Epic</TableCell>
                                <TableCell width="10%" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1 }}>Priority</TableCell>
                                <TableCell width="25%" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1 }}>Tasks Progress</TableCell>
                                <TableCell width="10%" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1 }}>Due Date</TableCell>
                                <TableCell width="10%" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1 }}>Status</TableCell>
                                <TableCell width="10%" align="center" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1 }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredStories.map((story) => (
                                <TableRow
                                    key={story.id}
                                    onClick={() => handleStoryRowClick(story)}
                                    sx={{
                                        '&:hover': { bgcolor: '#f5f5f5' },
                                        height: '56px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    <TableCell sx={{ py: 1 }}>
                                        <Box>
                                            <Typography variant="body2" fontWeight={500} noWrap sx={{ fontSize: '0.85rem' }}>
                                                {story.story_id}. {story.name}
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                                                <AccessTime fontSize="small" sx={{ fontSize: 12, mr: 0.5, color: 'text.secondary' }} />
                                                <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem' }}>
                                                    {story.completedTasks}/{story.tasks} tasks
                                                </Typography>
                                                <AvatarGroup max={3} sx={{ ml: 1, '& .MuiAvatar-root': { width: 18, height: 18, fontSize: 9 } }}>
                                                    {story.assignees.map((assignee, index) => (
                                                        <Tooltip key={index} title={assignee}>
                                                            <Avatar sx={{ width: 18, height: 18, fontSize: 9 }}>{assignee}</Avatar>
                                                        </Tooltip>
                                                    ))}
                                                </AvatarGroup>
                                            </Box>
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ py: 1 }}>
                                        <Chip
                                            label={story.epic}
                                            size="small"
                                            sx={{
                                                bgcolor: initialEpics.find(e => e.id === story.epicId)?.color + '20',
                                                color: initialEpics.find(e => e.id === story.epicId)?.color,
                                                border: '1px solid',
                                                borderColor: initialEpics.find(e => e.id === story.epicId)?.color,
                                                fontSize: '0.65rem',
                                                height: '20px'
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ py: 1 }}>
                                        <Chip
                                            label={story.priority}
                                            size="small"
                                            color={getPriorityColor(story.priority)}
                                            icon={story.priority === 'High' ? <PriorityHigh sx={{ fontSize: 12 }} /> : null}
                                            sx={{ fontSize: '0.65rem', height: '20px' }}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ py: 1 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Box sx={{ width: '100%', mr: 1 }}>
                                                <LinearProgress
                                                    variant="determinate"
                                                    value={story.progress}
                                                    sx={{
                                                        height: 6,
                                                        borderRadius: 3,
                                                        bgcolor: '#e0e0e0',
                                                        '& .MuiLinearProgress-bar': {
                                                            borderRadius: 3,
                                                            bgcolor: story.progress === 100 ? '#4caf50' : '#1976d2'
                                                        }
                                                    }}
                                                />
                                            </Box>
                                            <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.75rem', minWidth: '30px' }}>
                                                {story.progress}%
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ py: 1 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <CalendarToday sx={{ fontSize: 12, mr: 0.5, color: 'text.secondary' }} />
                                            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                                {new Date(story.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                            </Typography>
                                        </Box>
                                        <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem', ml: 1.5 }}>
                                            {new Date(story.dueDate).toLocaleDateString('en-US', { year: 'numeric' })}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ py: 1 }}>
                                        <Chip
                                            label={story.status}
                                            size="small"
                                            color={getStatusColor(story.status)}
                                            sx={{ fontSize: '0.65rem', height: '20px' }}
                                        />
                                    </TableCell>
                                    <TableCell align="center" sx={{ py: 1 }} onClick={(e) => e.stopPropagation()}>
                                        <Tooltip title="View Details">
                                            <IconButton
                                                size="small"
                                                onClick={(e) => handleViewStoryDetails(story.id, e)}
                                                sx={{
                                                    color: 'primary.main',
                                                    padding: '4px',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(25, 118, 210, 0.1)',
                                                    }
                                                }}
                                            >
                                                <Visibility fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            ) : viewMode === 'details' && currentStory ? (
                /* User Story Details View */
                <Box>
                    {/* Only show back arrow in details view */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <IconButton 
                            onClick={handleBackToStories} 
                            size="small"
                            sx={{ 
                                mr: 1,
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                }
                            }}
                        >
                            <ArrowBack fontSize="small" />
                        </IconButton>
                        <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.85rem' }}>
                            Back to User Stories
                        </Typography>
                    </Box>

                    {/* User Story Details Component */}
                    <UserStoryDetailsDummy
                        onClose={handleCloseStoryDetails}
                        storyId={currentStory.story_id}
                        onStoryDeleted={handleStoryDeleted}
                    />
                </Box>
            ) : viewMode === 'tasks' ? (
                /* Tasks View for Selected Story (Legacy) */
                <Box>
                    {/* Back button and story info */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <IconButton onClick={handleBackToStories} size="small" sx={{ mr: 1 }}>
                            <ArrowBack fontSize="small" />
                        </IconButton>
                        <Box>
                            <Typography variant="h6" sx={{ fontSize: '1rem' }}>
                                Tasks for: {currentStory?.name}
                            </Typography>
                            <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.8rem' }}>
                                Epic: {currentStory?.epic} • Due: {currentStory?.dueDate} • {currentStory?.completedTasks}/{currentStory?.tasks} tasks completed
                            </Typography>
                        </Box>
                    </Box>

                    {/* Tasks Table */}
                    <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 1 }}>
                        <Table sx={{ minWidth: 650 }} size="small">
                            <TableHead sx={{ bgcolor: '#fafafa' }}>
                                <TableRow sx={{ height: '40px' }}>
                                    <TableCell width="5%" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1 }}>#</TableCell>
                                    <TableCell width="50%" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1 }}>Task Name</TableCell>
                                    <TableCell width="15%" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1 }}>Status</TableCell>
                                    <TableCell width="15%" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1 }}>Assignee</TableCell>
                                    <TableCell width="15%" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1 }}>Estimated Hours</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {tasksData[selectedStory]?.map((task, index) => (
                                    <TableRow key={task.id} sx={{ '&:hover': { bgcolor: '#f5f5f5' }, height: '48px' }}>
                                        <TableCell sx={{ py: 1 }}>
                                            <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>{index + 1}</Typography>
                                        </TableCell>
                                        <TableCell sx={{ py: 1 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                {task.status === 'Completed' ? (
                                                    <CheckCircle sx={{ color: '#4caf50', fontSize: 14, mr: 1 }} />
                                                ) : (
                                                    <RadioButtonUnchecked sx={{ color: 'text.secondary', fontSize: 14, mr: 1 }} />
                                                )}
                                                <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>{task.name}</Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ py: 1 }}>
                                            <Chip
                                                label={task.status}
                                                size="small"
                                                sx={{
                                                    fontSize: '0.65rem',
                                                    height: '20px',
                                                    bgcolor: task.status === 'Completed' ? '#e8f5e9' :
                                                        task.status === 'In Progress' ? '#e3f2fd' : '#f5f5f5',
                                                    color: task.status === 'Completed' ? '#2e7d32' :
                                                        task.status === 'In Progress' ? '#1976d2' : '#757575'
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ py: 1 }}>
                                            <Chip
                                                label={task.assignee}
                                                size="small"
                                                sx={{ fontSize: '0.65rem', height: '20px' }}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ py: 1 }}>
                                            <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>{task.estimatedHours}h</Typography>
                                        </TableCell>
                                    </TableRow>
                                )) || (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                                            <Typography color="textSecondary">No tasks found for this story</Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            ) : null}

            {/* Create User Story Dialog */}
            <Dialog
                open={createDialogOpen}
                onClose={handleCloseCreateDialog}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                        overflow: 'hidden'
                    }
                }}
            >
                <DialogContent sx={{ p: 0 }}>
                    <CreateUserStory 
                        onComplete={handleUserStoryCreated}
                        epicName={currentEpicData?.epicName || 'Select an Epic first'}
                    />
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default UserStoryTable;